import { Trans, useLingui } from "@lingui/react/macro";
import type { WebsiteHost, WebsiteTeammate } from "@rakazo/contracts";
import { parseWebsitePaths, WEBSITE_HOST_LABELS, WEBSITE_HOSTS } from "@rakazo/contracts";
import {
  Button,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  NativeSelect,
  NativeSelectOption,
  Switch,
  Textarea,
} from "@rakazo/ui-web";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import type { ReactNode } from "react";
import { useId, useState } from "react";
import { BuiCard } from "../../components/ai/primitives";
import type { SpaceQueries } from "../../lib/query";
import { useSpaceRpc } from "../../lib/space-rpc";
import { errorText } from "../../lib/user-error";

const HOST_LABELS = WEBSITE_HOST_LABELS;

export const parsePaths = parseWebsitePaths;

/**
 * A Website teammate's section beside its chat. Other bots get nothing. Until the code and host
 * are connected it offers setup; after that it shows where it works. Its upkeep routine is in the
 * routine list like any other.
 */
export function BotWebsiteSection({
  botId,
  queries,
  title,
  onRoutinesChanged,
}: {
  botId: string;
  queries: SpaceQueries;
  title: ReactNode;
  onRoutinesChanged?: () => void;
}) {
  const queryClient = useQueryClient();
  const options = queries.websiteTeammate.get.queryOptions({ input: { botId } });
  const teammate = useQuery(options).data;
  const [open, setOpen] = useState(false);
  if (!teammate) return null;
  const refresh = (next: WebsiteTeammate) => {
    if (next.upkeep?.active !== teammate.upkeep?.active) onRoutinesChanged?.();
    queryClient.setQueryData(options.queryKey, next);
  };
  const connected = Boolean(teammate.siteUrl && teammate.repo && teammate.host);
  return (
    <div data-testid="bot-website">
      {title}
      {connected ? (
        <dl className="divide-y divide-border rounded-xl border border-border text-[13.5px]">
          <Row label={<Trans>Site</Trans>} value={teammate.siteUrl ?? ""} />
          <Row label={<Trans>Code</Trans>} value={teammate.repo ?? ""} />
          <Row
            label={<Trans>Host</Trans>}
            value={teammate.host ? HOST_LABELS[teammate.host] : ""}
          />
          <div className="px-3.5 py-2.5">
            <Button variant="ghost" size="sm" onClick={() => setOpen(true)}>
              <Trans>Edit</Trans>
            </Button>
          </div>
        </dl>
      ) : (
        <Button variant="outline" className="w-full" onClick={() => setOpen(true)}>
          <Trans>Connect code and host</Trans>
        </Button>
      )}
      {open ? (
        <WebsiteSetupDialog teammate={teammate} onClose={() => setOpen(false)} onSaved={refresh} />
      ) : null}
    </div>
  );
}

function Row({ label, value }: { label: ReactNode; value: string }) {
  return (
    <div className="flex items-center justify-between gap-3 px-3.5 py-2.5">
      <dt className="text-muted-foreground">{label}</dt>
      <dd className="min-w-0 truncate text-foreground">{value}</dd>
    </div>
  );
}

/** The daily upkeep, offered like an approval: nothing runs until the owner turns it on. */
export function UpkeepCard({
  teammate,
  onDone,
  onSkip,
}: {
  teammate: WebsiteTeammate;
  onDone: (next: WebsiteTeammate) => void;
  onSkip: () => void;
}) {
  const { t } = useLingui();
  const rpc = useSpaceRpc();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  return (
    <BuiCard className="space-y-3 p-4" data-testid="website-upkeep-card">
      <p className="text-[14px] font-medium text-foreground">
        <Trans>Turn on daily upkeep?</Trans>
      </p>
      <p className="text-[13px] text-muted-foreground">
        <Trans>Each morning: check the site, make one improvement, report.</Trans>
      </p>
      {error ? (
        <p role="alert" className="text-[13px] text-destructive">
          {error}
        </p>
      ) : null}
      <div className="flex gap-2">
        <Button
          disabled={busy}
          onClick={() => {
            setBusy(true);
            setError(null);
            rpc.websiteTeammate
              .upkeep({ botId: teammate.botId, active: true })
              .then(onDone, (err: unknown) => setError(errorText(err, t`Could not turn it on`)))
              .finally(() => setBusy(false));
          }}
        >
          <Trans>Turn on</Trans>
        </Button>
        <Button variant="outline" disabled={busy} onClick={onSkip}>
          <Trans>Not now</Trans>
        </Button>
      </div>
    </BuiCard>
  );
}

/** Code, then host, then the upkeep offer. Tokens go to the vault and are never shown again. */
export function WebsiteSetupDialog({
  teammate,
  onClose,
  onSaved,
}: {
  teammate: WebsiteTeammate;
  onClose: () => void;
  onSaved: (next: WebsiteTeammate) => void;
}) {
  const { t } = useLingui();
  const rpc = useSpaceRpc();
  const ids = useId();
  const [step, setStep] = useState<"code" | "host" | "upkeep">("code");
  const [siteUrl, setSiteUrl] = useState(teammate.siteUrl ?? "");
  const [repo, setRepo] = useState(teammate.repo ?? "");
  const [branch, setBranch] = useState(teammate.branch);
  const [githubToken, setGithubToken] = useState("");
  const [host, setHost] = useState<WebsiteHost>(teammate.host ?? "vercel");
  const [sshTarget, setSshTarget] = useState(teammate.sshTarget ?? "");
  const [hostToken, setHostToken] = useState("");
  const [paths, setPaths] = useState(teammate.protectedPaths.join(", "));
  const [directPush, setDirectPush] = useState(teammate.directPush);
  const [saved, setSaved] = useState(teammate);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const hostSaved = teammate.hostSaved && teammate.host === host;
  const savedHint = t`Saved`;

  const codeReady = siteUrl.trim() && repo.trim() && (githubToken || teammate.githubSaved);
  const hostReady = (hostToken || hostSaved) && (host !== "ssh" || sshTarget.trim());

  async function save() {
    setBusy(true);
    setError(null);
    try {
      const next = await rpc.websiteTeammate.save({
        botId: teammate.botId,
        siteUrl: siteUrl.trim(),
        repo: repo.trim(),
        branch: branch.trim() || "main",
        host,
        ...(host === "ssh" ? { sshTarget: sshTarget.trim() } : {}),
        protectedPaths: parsePaths(paths),
        directPush,
        ...(githubToken ? { githubToken } : {}),
        ...(hostToken ? { hostToken } : {}),
      });
      setGithubToken("");
      setHostToken("");
      setSaved(next);
      onSaved(next);
      if (next.upkeep && !next.upkeep.active) setStep("upkeep");
      else onClose();
    } catch (err) {
      setError(errorText(err, t`Could not save`));
    } finally {
      setBusy(false);
    }
  }

  const field = (id: string, label: ReactNode, control: ReactNode) => (
    <div className="space-y-1.5">
      <label htmlFor={id} className="text-[13px] text-muted-foreground">
        {label}
      </label>
      {control}
    </div>
  );

  return (
    <Dialog
      open
      onOpenChange={(next) => {
        if (!next && !busy) onClose();
      }}
    >
      <DialogContent
        aria-describedby={undefined}
        className="max-h-[calc(100dvh-2rem)] overflow-y-auto sm:max-w-md"
        data-testid="website-setup-dialog"
      >
        <DialogHeader>
          <DialogTitle>
            {step === "code" ? (
              <Trans>Connect the code</Trans>
            ) : step === "host" ? (
              <Trans>Connect the host</Trans>
            ) : (
              <Trans>Daily upkeep</Trans>
            )}
          </DialogTitle>
        </DialogHeader>
        {step === "code" ? (
          <form
            className="contents"
            onSubmit={(event) => {
              event.preventDefault();
              if (codeReady) setStep("host");
            }}
          >
            {field(
              `${ids}-site`,
              <Trans>Site</Trans>,
              <Input
                id={`${ids}-site`}
                type="url"
                placeholder="https://example.com"
                value={siteUrl}
                onChange={(event) => setSiteUrl(event.target.value)}
              />,
            )}
            {field(
              `${ids}-repo`,
              <Trans>GitHub repository</Trans>,
              <Input
                id={`${ids}-repo`}
                placeholder="owner/repository"
                value={repo}
                onChange={(event) => setRepo(event.target.value)}
              />,
            )}
            {field(
              `${ids}-branch`,
              <Trans>Branch</Trans>,
              <Input
                id={`${ids}-branch`}
                value={branch}
                onChange={(event) => setBranch(event.target.value)}
              />,
            )}
            {field(
              `${ids}-github`,
              <Trans>GitHub token</Trans>,
              <Input
                id={`${ids}-github`}
                type="password"
                autoComplete="off"
                placeholder={teammate.githubSaved ? savedHint : ""}
                value={githubToken}
                onChange={(event) => setGithubToken(event.target.value)}
              />,
            )}
            <DialogFooter>
              <Button type="button" variant="outline" onClick={onClose}>
                <Trans>Cancel</Trans>
              </Button>
              <Button type="submit" disabled={!codeReady}>
                <Trans>Next</Trans>
              </Button>
            </DialogFooter>
          </form>
        ) : step === "host" ? (
          <form
            className="contents"
            onSubmit={(event) => {
              event.preventDefault();
              if (hostReady) void save();
            }}
          >
            {field(
              `${ids}-host`,
              <Trans>Host</Trans>,
              <NativeSelect
                id={`${ids}-host`}
                className="w-full"
                value={host}
                onChange={(event) => setHost(event.target.value as WebsiteHost)}
              >
                {WEBSITE_HOSTS.map((value) => (
                  <NativeSelectOption key={value} value={value}>
                    {HOST_LABELS[value]}
                  </NativeSelectOption>
                ))}
              </NativeSelect>,
            )}
            {host === "ssh"
              ? field(
                  `${ids}-ssh`,
                  <Trans>Server</Trans>,
                  <Input
                    id={`${ids}-ssh`}
                    placeholder="user@host"
                    value={sshTarget}
                    onChange={(event) => setSshTarget(event.target.value)}
                  />,
                )
              : null}
            {host === "ssh"
              ? field(
                  `${ids}-token`,
                  <Trans>Private key</Trans>,
                  <Textarea
                    id={`${ids}-token`}
                    autoComplete="off"
                    spellCheck={false}
                    className="font-mono [-webkit-text-security:disc]"
                    placeholder={hostSaved ? savedHint : ""}
                    value={hostToken}
                    onChange={(event) => setHostToken(event.target.value)}
                  />,
                )
              : field(
                  `${ids}-token`,
                  <Trans>Deploy token</Trans>,
                  <Input
                    id={`${ids}-token`}
                    type="password"
                    autoComplete="off"
                    placeholder={hostSaved ? savedHint : ""}
                    value={hostToken}
                    onChange={(event) => setHostToken(event.target.value)}
                  />,
                )}
            <div className="flex items-center justify-between gap-3">
              <label htmlFor={`${ids}-direct`} className="text-[13px] font-medium">
                <Trans>Push without a pull request</Trans>
              </label>
              <Switch
                id={`${ids}-direct`}
                checked={directPush}
                onCheckedChange={(checked) => setDirectPush(checked)}
              />
            </div>
            {directPush
              ? field(
                  `${ids}-paths`,
                  <Trans>Pull request for</Trans>,
                  <Input
                    id={`${ids}-paths`}
                    placeholder="/, /pricing"
                    value={paths}
                    onChange={(event) => setPaths(event.target.value)}
                  />,
                )
              : null}
            {error ? (
              <p role="alert" className="text-[13px] text-destructive">
                {error}
              </p>
            ) : null}
            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                disabled={busy}
                onClick={() => setStep("code")}
              >
                <Trans>Back</Trans>
              </Button>
              <Button type="submit" disabled={!hostReady || busy}>
                <Trans>Save</Trans>
              </Button>
            </DialogFooter>
          </form>
        ) : (
          <UpkeepCard
            teammate={saved}
            onDone={(next) => {
              onSaved(next);
              onClose();
            }}
            onSkip={onClose}
          />
        )}
      </DialogContent>
    </Dialog>
  );
}
